import { useState, useEffect, ChangeEvent, useCallback, useMemo } from 'react';

export interface UseFieldType {
  actions: {
    onError: (errorMsg: string) => void;
    onSuccess: () => void;
    resetValue: () => void;
    changeValue: (newValue: string) => void;
  };
  attributes: {
    error: boolean;
    helperText: string;
    type: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
}

export const useFieldInitialState: UseFieldType = {
  actions: {
    onError: () => {},
    onSuccess: () => {},
    resetValue: () => {},
    changeValue: () => {},
  },
  attributes: {
    error: false,
    helperText: '',
    type: '',
    value: '',
    onChange: () => {},
  },
};

const useField = (type: string, initialValue?: string): UseFieldType => {
  const [value, setValue] = useState(initialValue || '');
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const onError = useCallback((errorMsg: string) => {
    setError(true);
    setHelperText(errorMsg);
  }, []);

  const onSuccess = useCallback(() => {
    setError(false);
    setHelperText('');
  }, []);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const changeValue = useCallback((newValue: string) => {
    setValue(newValue);
  }, []);

  const resetValue = useCallback(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const actions = useMemo(
    () => ({ onError, onSuccess, resetValue, changeValue }),
    [onError, onSuccess, resetValue, changeValue],
  );

  const attributes = useMemo(
    () => ({ error, helperText, type, value, onChange }),
    [error, helperText, type, value, onChange],
  );

  return { actions, attributes };
};

export default useField;
